/**
 * Race room state machine. Transport-agnostic so the Node server runs it over WebSockets
 * and the browser can run it in-process for offline practice.
 */
import { analyseKeystrokes, GENERAL_TIPS, keyStatsOf } from './hints.js';
import { ACCURACY_BONUS, pointsForPlace } from './league.js';
import {
  MAX_PLAYERS,
  PUBLIC_ROOM,
  sanitiseName,
  TEAM_COLOURS,
  TEAM_NAMES,
  type TeamResult,
  type ClientMessage,
  type PlayerInfo,
  type PlayerProgress,
  type RaceResult,
  type RacePhase,
  type RoomOptions,
  type ServerMessage,
} from './messages.js';
import { mulberry32, pickSentences } from './sentences.js';
import { PACKS, subjectById, subjectSentences } from './subjects.js';
import {
  accuracyOf,
  applyKey,
  createTypingState,
  currentSentence,
  DEFAULT_RACE_CONFIG,
  progressOf,
  summarise,
  wpmOf,
  type TypingState,
} from './typing.js';
import { COLOURS, DEFAULT_VEHICLE, isValidColour, vehicleById } from './vehicles.js';

export interface RoomHost {
  now(): number;
  send(id: string, msg: ServerMessage): void;
  broadcast(msg: ServerMessage): void;
  /** Persist a finished race and return the league. */
  recordRace(results: RaceResult[]): void;
  getLeague(): Extract<ServerMessage, { type: 'league' }>;
  log?(msg: string): void;
}

export const DEFAULT_ROOM_OPTIONS: RoomOptions = {
  ...DEFAULT_RACE_CONFIG,
  fillWithBots: true,
  botWpm: 45,
  pack: 'motivation',
  teams: 0,
};

export const COUNTDOWN_MS = 3000;
export const TICK_MS = 100;
/** After the first human finishes, the rest have this long before the race is called. */
export const FINISH_GRACE_MS = 30_000;
export const MAX_RACE_MS = 5 * 60_000;

const BOT_NAMES = ['Zoom Bot', 'Turbo Bot', 'Nitro Bot', 'Dash Bot', 'Bolt Bot', 'Rocket Bot'];

interface Player {
  info: PlayerInfo;
  state: TypingState | null;
  place: number | null;
  finishedAt: number | null;
  /** Bot simulation. */
  bot?: { wpm: number; owed: number; pendingFix: boolean; rng: () => number };
  /** Sliding window for rate limiting (timestamps of recent keys). */
  recentKeys: number[];
}

export class Room {
  phase: RacePhase = 'lobby';
  options: RoomOptions = { ...DEFAULT_ROOM_OPTIONS };
  private players = new Map<string, Player>();
  private sentences: string[] = [];
  private raceStartAt = 0;
  private firstFinishAt: number | null = null;
  private placesGiven = 0;
  private tickHandle: ReturnType<typeof setInterval> | null = null;
  private countdownHandle: ReturnType<typeof setTimeout> | null = null;
  private tipIndex = 0;
  private lastResults: Extract<ServerMessage, { type: 'results' }> | null = null;

  constructor(
    private host: RoomHost,
    public readonly code: string = PUBLIC_ROOM,
  ) {}

  get isPublic(): boolean {
    return this.code === PUBLIC_ROOM;
  }

  // ---------------------------------------------------------------- queries
  get humanCount(): number {
    return [...this.players.values()].filter((p) => !p.info.isBot).length;
  }

  get playerList(): PlayerInfo[] {
    return [...this.players.values()].map((p) => p.info);
  }

  hasPlayer(id: string): boolean {
    return this.players.has(id);
  }

  /** State for a player reconnecting mid-race, or null when there is nothing to resume. */
  resumeFor(id: string): Extract<ServerMessage, { type: 'resume' }> | null {
    const p = this.players.get(id);
    if (!p?.state || (this.phase !== 'countdown' && this.phase !== 'racing')) return null;
    const st = p.state;
    return {
      type: 'resume',
      phase: this.phase,
      sentences: this.sentences,
      startAt: this.raceStartAt,
      state: {
        sentenceIndex: st.sentenceIndex,
        cursor: st.cursor,
        committedChars: st.committedChars,
        bonusChars: st.bonusChars,
        errorBuffer: st.errorBuffer,
        streak: st.streak,
        streaksCompleted: st.streaksCompleted,
        stallUntil: st.stallUntil,
        correctCount: st.correctCount,
        errorCount: st.errorCount,
        backspaceCount: st.backspaceCount,
        startedAt: st.startedAt,
        finishedAt: st.finishedAt,
        place: p.place,
      },
    };
  }

  resultsFor(id: string): Extract<ServerMessage, { type: 'results' }> | null {
    return this.players.has(id) && this.phase === 'results' ? this.lastResults : null;
  }

  // --------------------------------------------------------------- messages
  handle(id: string, msg: ClientMessage): void {
    switch (msg.type) {
      case 'leave':
        return this.leave(id);
      case 'join':
        return this.join(id, msg.name, msg.vehicle, msg.colour);
      case 'createRoom':
        return this.join(id, msg.name, msg.vehicle, msg.colour);
      case 'setTeam':
        return this.setTeam(id, msg.team);
      case 'setVehicle':
        return this.setVehicle(id, msg.vehicle, msg.colour);
      case 'setOptions':
        return this.setOptions(id, msg.options);
      case 'ready':
        return this.setReady(id, msg.ready);
      case 'start':
        return this.start(id);
      case 'key':
        return this.key(id, msg.key);
      case 'backToLobby':
        return this.backToLobby(id);
      case 'requestLeague':
        return this.host.send(id, this.host.getLeague());
      case 'requestLobby':
        return this.broadcastLobby();
      case 'ping':
        return this.host.send(id, { type: 'pong', t: msg.t, serverTime: this.host.now() });
    }
  }

  join(id: string, rawName: string, vehicle: string, colour: string): void {
    if (this.players.has(id)) return;
    const name = sanitiseName(rawName);
    if (!name) return this.host.send(id, { type: 'error', message: 'Name must be 2-16 letters or numbers.' });
    if (this.humanCount >= MAX_PLAYERS) return this.host.send(id, { type: 'error', message: 'Room is full (6 players).' });
    if (this.phase !== 'lobby' && this.phase !== 'results') {
      return this.host.send(id, { type: 'error', message: 'A race is in progress. Wait for it to finish.' });
    }
    const taken = [...this.players.values()].some(
      (p) => !p.info.isBot && p.info.name.toLowerCase() === name.toLowerCase(),
    );
    if (taken) return this.host.send(id, { type: 'error', message: 'That name is already in the room.' });

    // Evict a bot if we're at the cap including bots.
    if (this.players.size >= MAX_PLAYERS) {
      const bot = [...this.players.values()].find((p) => p.info.isBot);
      if (bot) this.players.delete(bot.info.id);
    }

    const isHost = this.humanCount === 0;
    this.players.set(id, {
      info: {
        id,
        name,
        vehicle: vehicleById(vehicle).id,
        colour: isValidColour(colour) ? colour : COLOURS[0]!.hex,
        isBot: false,
        isHost,
        ready: false,
        team: null,
      },
      state: null,
      place: null,
      finishedAt: null,
      recentKeys: [],
    });
    this.balanceTeams();
    this.host.send(id, { type: 'welcome', id, serverTime: this.host.now() });
    this.broadcastLobby();
  }

  /** Put every player on a team when team mode is on; smallest team first. */
  private balanceTeams(): void {
    const n = this.options.teams;
    for (const p of this.players.values()) {
      if (n === 0) p.info.team = null;
      else if (p.info.team === null || p.info.team >= n) p.info.team = this.smallestTeam();
    }
  }

  private smallestTeam(): number {
    const counts = Array.from({ length: this.options.teams }, () => 0);
    for (const p of this.players.values()) if (p.info.team !== null && p.info.team < counts.length) counts[p.info.team]!++;
    let best = 0;
    for (let i = 1; i < counts.length; i++) if (counts[i]! < counts[best]!) best = i;
    return best;
  }

  private setTeam(id: string, team: number): void {
    const p = this.players.get(id);
    if (!p || this.phase !== 'lobby' || this.options.teams === 0) return;
    if (!Number.isInteger(team) || team < 0 || team >= this.options.teams) return;
    p.info.team = team;
    this.broadcastLobby();
  }

  leave(id: string): void {
    const p = this.players.get(id);
    if (!p) return;
    this.players.delete(id);
    if (p.info.isHost) {
      const next = [...this.players.values()].find((q) => !q.info.isBot);
      if (next) next.info.isHost = true;
    }
    if (this.humanCount === 0) {
      this.resetToLobby();
      return;
    }
    if (this.phase === 'racing') this.maybeFinishRace();
    this.broadcastLobby();
  }

  private setVehicle(id: string, vehicle: string, colour: string): void {
    const p = this.players.get(id);
    if (!p || this.phase === 'racing' || this.phase === 'countdown') return;
    p.info.vehicle = vehicleById(vehicle).id;
    if (isValidColour(colour)) p.info.colour = colour;
    this.broadcastLobby();
  }

  private setOptions(id: string, opts: Partial<RoomOptions>): void {
    const p = this.players.get(id);
    if (!p?.info.isHost || this.phase !== 'lobby') return;
    const o = this.options;
    if (typeof opts.sentenceCount === 'number') o.sentenceCount = clamp(Math.round(opts.sentenceCount), 1, 15);
    if (typeof opts.fillWithBots === 'boolean') o.fillWithBots = opts.fillWithBots;
    if (typeof opts.botWpm === 'number') o.botWpm = clamp(Math.round(opts.botWpm), 10, 150);
    if (typeof opts.typoStallMs === 'number') o.typoStallMs = clamp(opts.typoStallMs, 0, 2000);
    if (typeof opts.pack === 'string' && PACKS.some((p) => p.id === opts.pack)) o.pack = opts.pack;
    if (opts.teams === 0 || opts.teams === 2 || opts.teams === 3) {
      o.teams = opts.teams;
      this.balanceTeams();
    }
    this.broadcastLobby();
  }

  private setReady(id: string, ready: boolean): void {
    const p = this.players.get(id);
    if (!p || this.phase !== 'lobby') return;
    p.info.ready = ready;
    this.broadcastLobby();
  }

  private start(id: string): void {
    const p = this.players.get(id);
    if (!p?.info.isHost || this.phase !== 'lobby') return;
    this.beginCountdown();
  }

  private backToLobby(id: string): void {
    const p = this.players.get(id);
    if (!p || this.phase !== 'results') return;
    this.resetToLobby();
  }

  // --------------------------------------------------------------- race flow
  private beginCountdown(): void {
    // Remove old bots, then fill.
    this.removeBots();
    if (this.options.fillWithBots) {
      const seed = Math.floor(this.host.now()) ^ 0x9e3779b9;
      let i = 0;
      while (this.players.size < MAX_PLAYERS) {
        const rng = mulberry32(seed + i * 7919);
        const botId = `bot-${i}`;
        const wpm = this.options.botWpm * (0.75 + rng() * 0.5);
        this.players.set(botId, {
          info: {
            id: botId,
            name: BOT_NAMES[i] ?? `Bot ${i + 1}`,
            vehicle: vehicleById(['race', 'truck', 'van', 'taxi', 'kart', 'bus'][i] ?? DEFAULT_VEHICLE).id,
            colour: COLOURS[(i + 3) % COLOURS.length]!.hex,
            isBot: true,
            isHost: false,
            ready: true,
            team: null,
          },
          state: null,
          place: null,
          finishedAt: null,
          bot: { wpm, owed: 0, pendingFix: false, rng },
          recentKeys: [],
        });
        i++;
      }
    }
    this.balanceTeams();

    const seed = Math.floor(this.host.now()) % 2147483647;
    const subject = subjectById(this.options.pack);
    this.sentences = subject
      ? pickSentences(this.options.sentenceCount, seed, subjectSentences(subject))
      : pickSentences(this.options.sentenceCount, seed);
    const cfg = {
      sentenceCount: this.options.sentenceCount,
      typoStallMs: this.options.typoStallMs,
      streakLength: this.options.streakLength,
      streakBonusChars: this.options.streakBonusChars,
    };
    for (const p of this.players.values()) {
      p.state = createTypingState(this.sentences, cfg);
      p.place = null;
      p.finishedAt = null;
      p.info.ready = false;
      if (p.bot) p.bot.owed = -(2 + p.bot.rng() * 3) * 5; // bots "read" for a moment first
    }
    this.placesGiven = 0;
    this.firstFinishAt = null;
    this.phase = 'countdown';
    const startAt = this.host.now() + COUNTDOWN_MS;
    this.raceStartAt = startAt;
    const tip = GENERAL_TIPS[this.tipIndex++ % GENERAL_TIPS.length]!;
    this.host.broadcast({ type: 'countdown', seconds: COUNTDOWN_MS / 1000, sentences: this.sentences, startAt, tip });
    this.broadcastLobby();
    this.countdownHandle = setTimeout(() => this.go(), COUNTDOWN_MS);
  }

  private go(): void {
    this.countdownHandle = null;
    this.phase = 'racing';
    this.raceStartAt = this.host.now();
    this.host.broadcast({ type: 'go', startAt: this.raceStartAt });
    this.tickHandle = setInterval(() => this.tick(), TICK_MS);
  }

  private key(id: string, key: string): void {
    const p = this.players.get(id);
    if (!p?.state || this.phase !== 'racing' || p.finishedAt !== null) return;
    if (key !== 'Backspace' && key.length !== 1) return;
    const now = this.host.now();
    // Rate limit: > 30 keys/s sustained over a second is beyond human (~360 WPM).
    p.recentKeys.push(now);
    while (p.recentKeys.length && p.recentKeys[0]! < now - 1000) p.recentKeys.shift();
    if (p.recentKeys.length > 30) return;
    this.applyAndCheck(p, key, now);
  }

  private applyAndCheck(p: Player, key: string, now: number): void {
    if (!p.state) return;
    const ev = applyKey(p.state, key, now);
    if (ev === 'finished') this.onFinished(p, now);
  }

  private onFinished(p: Player, now: number): void {
    p.finishedAt = now;
    p.place = ++this.placesGiven;
    if (this.firstFinishAt === null && !p.info.isBot) this.firstFinishAt = now;
    this.host.broadcast({ type: 'playerFinished', id: p.info.id, place: p.place });
    this.maybeFinishRace();
  }

  private tick(): void {
    if (this.phase !== 'racing') return;
    const now = this.host.now();
    for (const p of this.players.values()) if (p.bot && p.state && p.finishedAt === null) this.stepBot(p, now);
    this.host.broadcast({ type: 'progress', players: this.progressList(now), serverTime: now });
    if (now - this.raceStartAt > MAX_RACE_MS) this.finishRace();
    else if (this.firstFinishAt !== null && now - this.firstFinishAt > FINISH_GRACE_MS) this.finishRace();
  }

  private stepBot(p: Player, now: number): void {
    const bot = p.bot!;
    const state = p.state!;
    const cps = (bot.wpm * 5) / 60;
    bot.owed += (cps * TICK_MS) / 1000 * (0.85 + bot.rng() * 0.3);
    while (bot.owed >= 1 && p.finishedAt === null) {
      bot.owed -= 1;
      if (state.errorBuffer.length > 0) {
        this.applyAndCheck(p, 'Backspace', now);
        continue;
      }
      const expected = currentSentence(state)[state.cursor] ?? '';
      const typo = bot.rng() < 0.025;
      this.applyAndCheck(p, typo ? neighbour(expected, bot.rng) : expected, now);
    }
  }

  private progressList(now: number): PlayerProgress[] {
    return [...this.players.values()].map((p) => ({
      id: p.info.id,
      progress: p.state ? progressOf(p.state) : 0,
      wpm: p.state ? Math.round(wpmOf(p.state, now)) : 0,
      accuracy: p.state ? accuracyOf(p.state) : 1,
      sentenceIndex: p.state?.sentenceIndex ?? 0,
      cursor: p.state?.cursor ?? 0,
      finished: p.finishedAt !== null,
      place: p.place,
    }));
  }

  private maybeFinishRace(): void {
    const humans = [...this.players.values()].filter((p) => !p.info.isBot);
    if (humans.length > 0 && humans.every((p) => p.finishedAt !== null)) this.finishRace();
  }

  private finishRace(): void {
    if (this.phase !== 'racing') return;
    if (this.tickHandle) clearInterval(this.tickHandle);
    this.tickHandle = null;
    this.phase = 'results';
    const now = this.host.now();

    // Unfinished players are placed by progress.
    const unfinished = [...this.players.values()]
      .filter((p) => p.finishedAt === null)
      .sort((a, b) => progressOf(b.state!) - progressOf(a.state!));
    for (const p of unfinished) p.place = ++this.placesGiven;

    const all = [...this.players.values()].sort((a, b) => a.place! - b.place!);
    const humanAcc = all.filter((p) => !p.info.isBot).map((p) => accuracyOf(p.state!));
    const bestAcc = humanAcc.length ? Math.max(...humanAcc) : -1;

    const results: RaceResult[] = all.map((p) => {
      const summary = summarise(p.state!, now);
      const bestAccuracy = !p.info.isBot && summary.accuracy >= bestAcc - 1e-9 && humanAcc.length > 1;
      const points = p.info.isBot ? 0 : pointsForPlace(p.place!) + (bestAccuracy ? ACCURACY_BONUS : 0);
      return {
        id: p.info.id,
        name: p.info.name,
        vehicle: p.info.vehicle,
        colour: p.info.colour,
        isBot: p.info.isBot,
        place: p.place!,
        summary,
        points,
        bestAccuracy,
        hints: p.info.isBot ? [] : analyseKeystrokes(p.state!.keystrokes),
        keyStats: p.info.isBot ? [] : keyStatsOf(p.state!.keystrokes),
      };
    });

    try {
      this.host.recordRace(results);
    } catch (e) {
      this.host.log?.(`recordRace failed: ${String(e)}`);
    }
    this.lastResults = { type: 'results', results, teams: this.teamResults(all), sentences: [...this.sentences] };
    this.host.broadcast(this.lastResults);
    this.host.broadcast(this.host.getLeague());
  }

  private teamResults(all: Player[]): TeamResult[] | null {
    const n = this.options.teams;
    if (n === 0) return null;
    const teams: TeamResult[] = [];
    for (let t = 0; t < n; t++) {
      const members = all.filter((p) => p.info.team === t);
      teams.push({
        team: t,
        name: TEAM_NAMES[t] ?? `Team ${t + 1}`,
        colour: TEAM_COLOURS[t] ?? '#999999',
        score: members.reduce((sum, p) => sum + pointsForPlace(p.place!), 0),
        members: members.map((p) => p.info.name),
        place: 0,
      });
    }
    // Tie-break: the team whose best member finished higher.
    const bestPlace = (t: TeamResult) => Math.min(...all.filter((p) => p.info.team === t.team).map((p) => p.place!), Infinity);
    teams.sort((a, b) => b.score - a.score || bestPlace(a) - bestPlace(b));
    teams.forEach((t, i) => (t.place = i + 1));
    return teams;
  }

  private resetToLobby(): void {
    this.lastResults = null;
    if (this.tickHandle) clearInterval(this.tickHandle);
    if (this.countdownHandle) clearTimeout(this.countdownHandle);
    this.tickHandle = null;
    this.countdownHandle = null;
    this.removeBots();
    for (const p of this.players.values()) {
      p.state = null;
      p.place = null;
      p.finishedAt = null;
      p.info.ready = false;
    }
    this.phase = 'lobby';
    this.broadcastLobby();
  }

  private removeBots(): void {
    const bots = this.playerList.filter((p) => p.isBot).map((p) => p.id);
    for (const id of bots) this.players.delete(id);
  }

  private broadcastLobby(): void {
    this.host.broadcast({
      type: 'lobby',
      players: this.playerList,
      options: { ...this.options },
      phase: this.phase,
      room: { code: this.code, isPublic: this.isPublic },
    });
  }

  dispose(): void {
    if (this.tickHandle) clearInterval(this.tickHandle);
    if (this.countdownHandle) clearTimeout(this.countdownHandle);
  }
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

const ROWS = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm'];
/** A plausible typo: a key adjacent to the expected one. */
function neighbour(ch: string, rng: () => number): string {
  const lower = ch.toLowerCase();
  for (const row of ROWS) {
    const i = row.indexOf(lower);
    if (i >= 0) {
      const j = clamp(i + (rng() < 0.5 ? -1 : 1), 0, row.length - 1);
      const n = row[j]!;
      return ch === lower ? n : n.toUpperCase();
    }
  }
  return ch === ' ' ? 'n' : 'x';
}
